/**
 * Index policy: which generated URLs are worth an index slot.
 *
 * Google reported ~204 "Crawled – currently not indexed" pages, almost all of
 * them ultra-low-demand combinations (exotic/defunct makes, long-dead models,
 * and cross-segment compare pairs). Publishing them dilutes crawl budget and
 * makes the site look auto-generated, so they stay reachable for users but are
 * marked noindex and dropped from the sitemap.
 */

/**
 * Makes with negligible US search demand or no current retail presence.
 *
 * Exotic-but-real brands that still sell in the US and have depth in our data
 * (Ferrari, Lamborghini, Maserati, Bentley, Rolls-Royce, Aston Martin,
 * McLaren) were removed from this list: they carry genuine search demand and
 * 44-147 trim rows each, so their hubs are worth an index slot.
 */
export const LOW_DEMAND_MAKES = new Set([
  "pagani",
  "koenigsegg",
  "karma",
  "lordstown",
  "fisker",
  "roush-performance",
  "saleen",
  "shelby",
  "spyker",
  "scion",
  "smart",
  "srt",
  "ruf",
  "pininfarina",
  "mobility-ventures-llc",
  "vpg",
  "azure-dynamics",
  "elio",
  "e-mobil",
  "kandi",
]);

/**
 * Model years older than this are historical archive, not search demand.
 *
 * Measured against our own Search Console table: models whose latest year is
 * 2018-19 are 49 nameplates pulling 716 peak impressions with 32 of them
 * already ranking inside the top 20 — real demand. Everything 2017 and older
 * is 82 nameplates, fewer impressions, and zero clicks. So the floor sits at
 * eight years back, and individual survivors older than that (Evora,
 * Cullinan) are rescued by recorded performance instead.
 */
export const MIN_INDEXABLE_LATEST_YEAR = new Date().getFullYear() - 8;

/** Fewer trims than this means there is not enough on the page to rank. */
export const MIN_INDEXABLE_ROWS = 3;

/**
 * Per-attribute row floors.
 *
 * The generic floor of 3 rows assumes year-over-year EPA rows. NHTSA safety
 * ratings publish one complete star-rating record per model year (overall,
 * frontal, side, rollover, plus crash-test notes), so a single row is already
 * a full, unique, high-demand page.
 */
export const ATTRIBUTE_ROW_FLOORS: Record<string, number> = {
  "safety-ratings": 1,
};

/**
 * Differentiation floor.
 *
 * Length is not the test Google applies — "scaled content abuse" is about many
 * pages that are the same page with a variable swapped in. A generated page
 * therefore has to carry a minimum number of *model-specific* facts (figures
 * or intervals that come from this vehicle's own data and are not shared with
 * every sibling page) before it earns an index slot, independent of word
 * count.
 */
export const MIN_DIFFERENTIATORS = 3;

/** Count the model-specific signals behind a page. Nulls do not count. */
export function countDifferentiators(signals: Array<unknown>): number {
  return signals.filter((s) => s !== null && s !== undefined && s !== false && s !== "").length;
}

export function isMainstreamMake(makeSlug: string): boolean {
  return !LOW_DEMAND_MAKES.has(makeSlug);
}

/**
 * Brand-level hub pages (e.g. /kia/maintenance-schedule) only carry their own
 * weight when the brand has enough models behind them to make the page
 * genuinely different from every other brand's. EPA "makes" like STI or Kandi
 * carry a single model and produce a page with nothing brand-specific on it.
 */
export const MIN_BRAND_PAGE_MODELS = 4;
/** A brand with few nameplates but deep trim data (Ram, Rivian) still counts. */
export const MIN_BRAND_PAGE_ROWS = 100;

export function isIndexableBrandPage(
  makeSlug: string,
  modelCount: number,
  rowCount = 0,
): boolean {
  if (!isMainstreamMake(makeSlug)) return false;
  return modelCount >= MIN_BRAND_PAGE_MODELS || rowCount >= MIN_BRAND_PAGE_ROWS;
}

/**
 * Ingest buckets that are not real nameplates. "BMW M" is a performance
 * sub-brand spanning M2/M3/M4/M5/M8, so EPA rows for all of them collapsed
 * into one phantom model. It cannot be compared, described, or scheduled as a
 * single vehicle, so it stays out of the index, the sitemap, and every
 * compare pair until the rows are re-split into real nameplates.
 */
export const NON_NAMEPLATE_MODELS = new Set(["bmw/m"]);

export function isNonNameplateModel(makeSlug: string, modelSlug?: string): boolean {
  return !!modelSlug && NON_NAMEPLATE_MODELS.has(`${makeSlug}/${modelSlug}`);
}


/**
 * Pages that already rank and earn clicks in Search Console. Real ranking data
 * beats a heuristic, so these override the make / year / trim-count rules and
 * stay indexed and in the sitemap.
 */
export const PROVEN_PERFORMER_MODELS = new Set([
  "vinfast/vf-6",
  "vinfast/vf-7",
  "ford/gt",
  "audi/sq5",
  "audi/allroad",
  "toyota/bz",
  "toyota/bz4x",
  "toyota/gr-supra",
  "aston-martin/vanquish",
  "ferrari/sf90",
  "alfa-romeo/tonale",
  "chevrolet/silverado",
  "toyota/sienna",
  "toyota/prius-v",
  "honda/pilot",
  "mitsubishi/eclipse-cross",
  // Heavy-duty pickups are exempt from EPA fuel-economy testing, so they carry
  // few EPA rows while having very high search demand. Their pages are built
  // from vPIC specs and NHTSA data instead.
  "ford/f250",
  "ford/f350",
  // Collector supercars keep steady search demand long after production ends,
  // so they are exempt from the "latest model year" recency floor.
  "ferrari/458-speciale",
  "lotus/evora",
  "rolls-royce/cullinan",
]);


/** Exact attribute URLs that rank despite their data type being low-value. */
export const PROVEN_PERFORMER_PATHS = new Set([
  "/chevrolet/silverado/fuel-tank-capacity",
  "/toyota/sienna/transmission",
  "/mitsubishi/eclipse-cross/dimensions",
  "/audi/sq5/dimensions",
  // Pacifica Hybrid is the only mainstream plug-in minivan; its EV-range page
  // has real demand even though the EPA row count is small.
  "/chrysler/pacifica/ev-range",
  "/land-rover/range-rover/ev-range",
]);

export function isProvenPerformerModel(makeSlug: string, modelSlug?: string): boolean {
  return !!modelSlug && PROVEN_PERFORMER_MODELS.has(`${makeSlug}/${modelSlug}`);
}

export function isProvenPerformerPath(path: string): boolean {
  return PROVEN_PERFORMER_PATHS.has(path);
}

export function isIndexableModel(input: {
  makeSlug: string;
  modelSlug?: string;
  latestYear: number | null;
  rowCount: number;
}): boolean {
  if (isNonNameplateModel(input.makeSlug, input.modelSlug)) return false;
  if (isProvenPerformerModel(input.makeSlug, input.modelSlug)) return true;
  if (!isMainstreamMake(input.makeSlug)) return false;
  if (input.rowCount < MIN_INDEXABLE_ROWS) return false;
  if (input.latestYear == null || input.latestYear < MIN_INDEXABLE_LATEST_YEAR) return false;
  return true;
}

/** Meta entries to spread into a route head() when a page should not be indexed. */
export const NOINDEX_META = [
  { name: "robots", content: "noindex, follow" },
] as const;

/**
 * Attribute pages that repeat one column of the parent model page. Google
 * reported ~5.2k of these as "Discovered – currently not indexed": they are
 * near-duplicates with no independent search demand, so they stay reachable
 * for users but are kept out of the index and the sitemap.
 */
export const LOW_VALUE_DATA_TYPES = new Set([
  "fuel-cost",
  "transmission",
  "drivetrain",
  "vehicle-class",
  "curb-weight",
]);

/**
 * Low-value types that come back for demand-qualified nameplates. curb-weight
 * is absent because it is generated from the dimensions table and handled by
 * the conditional-page branch of the sitemap.
 */
export const DEMAND_UNLOCKED_DATA_TYPE_SLUGS = [
  "fuel-cost",
  "transmission",
  "drivetrain",
  "vehicle-class",
];

/** EPA-derived attribute pages worth a sitemap slot. */
export const SITEMAP_DATA_TYPE_SLUGS = ["mpg", "engine", "maintenance-schedule"];

export function isIndexableDataType(dataType: string): boolean {
  return !LOW_VALUE_DATA_TYPES.has(dataType);
}

/**
 * Row-count floor for the auto-generated attribute pages (dimensions,
 * safety-ratings, ev-range, curb-weight...).
 *
 * These are generated from whatever NHTSA/EPA rows happen to exist, so a model
 * with one dimension row produces a page with a single line of data. Google's
 * scaled-content-abuse policy is about pages that exist because a URL pattern
 * allowed them, which is exactly this case, so anything under
 * MIN_INDEXABLE_ROWS rows *of the data backing that attribute* is kept out of
 * the index and the sitemap. Real Search Console performance always wins:
 * proven paths / models bypass the floor entirely.
 */
export function isIndexableAttributePage(input: {
  makeSlug: string;
  modelSlug: string;
  dataType: string;
  rowCount: number;
  latestYear: number | null;
  /** Live Search Console verdict for this exact path, when known. */
  provenByData?: boolean;
  /**
   * How many model-specific facts the rendered page carries. Required so the
   * sitemap and the live robots tag can never disagree: a caller that forgets
   * to compute differentiators fails at typecheck instead of silently indexing
   * a low-differentiation page.
   */
   differentiators: number;
  /**
   * Does this nameplate clear the demand threshold (see demand-index.server)?
   * Only lifts the low-value data-type block — every other floor still runs.
   */
  highDemandModel?: boolean;
}): boolean {
  const path = `/${input.makeSlug}/${input.modelSlug}/${input.dataType}`;
  if (isNonNameplateModel(input.makeSlug, input.modelSlug)) return false;
  if (input.provenByData) return true;
  if (isProvenPerformerPath(path)) return true;
  if (isProvenPerformerModel(input.makeSlug, input.modelSlug)) return true;
  if (!isIndexableDataType(input.dataType) && !input.highDemandModel) return false;
  if (!isMainstreamMake(input.makeSlug)) return false;
  const rowFloor = ATTRIBUTE_ROW_FLOORS[input.dataType] ?? MIN_INDEXABLE_ROWS;
  if (input.rowCount < rowFloor) return false;
  if (input.latestYear == null || input.latestYear < MIN_INDEXABLE_LATEST_YEAR) return false;
  const diffFloor = rowFloor < MIN_INDEXABLE_ROWS ? 2 : MIN_DIFFERENTIATORS;
  if (input.differentiators < diffFloor) {
    return false;
  }
  return true;
}

/** Raw row shapes used by scoreConditionalDifferentiators. */
export type ConditionalDimensionRow = {
  length_in?: number | null;
  width_in?: number | null;
  height_in?: number | null;
  wheelbase_in?: number | null;
  curb_weight_lbs?: number | null;
};

export type ConditionalSafetyRow = {
  overall_rating?: number | string | null;
  overall_front_rating?: number | string | null;
  overall_side_rating?: number | string | null;
  rollover_rating?: number | string | null;
  rollover_possibility?: number | string | null;
  recalls_count?: number | null;
  nhtsa_complaints_count?: number | null;
  investigation_count?: number | null;
  has_forward_collision_warning?: boolean | null;
  has_lane_departure_warning?: boolean | null;
};

export type ConditionalEvRow = {
  ev_range_miles?: number | null;
  ev_range_city?: number | null;
  ev_range_hwy?: number | null;
  ev_motor?: string | null;
  charge_120v_hours?: number | null;
  charge_240v_hours?: number | null;
  mpg_combined?: number | null;
  trim?: string | null;
};

/**
 * Score model-specific facts for the conditional attribute pages that the
 * sitemap builds from vehicle_dimensions, safety_ratings, and vehicles EV rows.
 * Mirrors the logic in the route's attributeDifferentiators so the sitemap and
 * the live page agree on whether the page has enough differentiation to index.
 */
export function scoreConditionalDifferentiators(
  dataType: string,
  rows: Array<ConditionalDimensionRow | ConditionalSafetyRow | ConditionalEvRow>,
): number {
  if (dataType === "ev-range") {
    const ev = rows as ConditionalEvRow[];
    const e = ev[0];
    return countDifferentiators([
      e?.ev_range_miles ?? null,
      e?.ev_range_city ?? null,
      e?.ev_range_hwy ?? null,
      e?.ev_motor ?? null,
      e?.charge_120v_hours ?? null,
      e?.charge_240v_hours ?? null,
      e?.mpg_combined ?? null,
      e?.trim ?? null,
      ev.length > 1 ? ev.length : null,
      new Set(ev.map((v) => v.ev_range_miles)).size > 1 ? "range-spread" : null,
    ]);
  }
  if (dataType === "safety-ratings") {
    const safety = rows as ConditionalSafetyRow[];
    const r = safety[0];
    return countDifferentiators([
      r?.overall_rating ?? null,
      r?.overall_front_rating ?? null,
      r?.overall_side_rating ?? null,
      r?.rollover_rating ?? null,
      safety.length > 1 ? safety.length : null,
      r?.rollover_possibility ?? null,
      r?.recalls_count ? r.recalls_count : null,
      r?.nhtsa_complaints_count ? r.nhtsa_complaints_count : null,
      r?.investigation_count ? r.investigation_count : null,
      r?.has_forward_collision_warning ?? null,
      r?.has_lane_departure_warning ?? null,
    ]);
  }
  if (dataType === "dimensions" || dataType === "curb-weight") {
    const dimensions = rows as ConditionalDimensionRow[];
    const d = dimensions[0];
    return countDifferentiators([
      d?.length_in ?? null,
      d?.width_in ?? null,
      d?.height_in ?? null,
      d?.wheelbase_in ?? null,
      d?.curb_weight_lbs ?? null,
      dimensions.length > 1 ? dimensions.length : null,
    ]);
  }
  return 0;
}
